<template>
  <div>
    <v-row>
      <v-col cols="12">
        <p class="text-2xl">
          Comissão de Afiliados
        </p>
        <v-card flat>
          <v-card-text class="pa-3">
            <v-row>
              <v-col
                cols="12"
                md="4"
                sm="6"
              >
                <v-select
                  v-model="filterServiceId"
                  :items="services"
                  label="Serviço"
                  outlined
                  dense
                  hide-details
                  item-text="name"
                  item-value="id"
                  @change="fetchCommissions"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-3">
            <v-btn
              color="primary"
              class="pl-4"
              @click="fetchCommissions"
            >
              Pesquisar
              <v-icon
                right
                dark
              >
                {{ icons.mdiMagnify }}
              </v-icon>
            </v-btn>
            <v-btn
              color="primary"
              class="pl-4"
              outlined
              @click="openFormToCreate"
            >
              Novo Registro
              <v-icon
                right
                dark
              >
                {{ icons.mdiTablePlus }}
              </v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card
          flat
          class="mt-5"
        >
          <v-data-table
            :headers="headers"
            :items="rows"
            item-key="id"
            class="table-rounded"
            disable-sort
            dense
          >
            <template #[`item.action`]="{item}">
              <v-btn
                color="success"
                dark
                text
                title="Editar"
                x-small
                @click="openFormToEdit(item.id)"
              >
                <v-icon>{{ icons.mdiFileEdit }}</v-icon>
              </v-btn>
            </template>
            <template #[`item.serviceId`]="{item}">
              {{ resolveServiceName(item.serviceId) }}
            </template>
            <template #[`item.percentage`]="{item}">
              {{ $money(item.percentage, 0) }} %
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <validation-observer
      ref="formAffiliate"
      autocomplete="off"
      tag="form"
      @keyup.enter="fetchAction"
    >
      <dialog-form
        :status="formDialogStatus"
        :is-update="formIsUpdate"
        :title="'Comissão de Afiliado'"
        :width="900"
        @action="fetchAction"
        @remove="removeDialogStatus = true"
        @close="formDialogStatus = !formDialogStatus"
      >
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Serviço"
              rules="required"
              vid="form.serviceId"
            >
              <v-select
                v-model="form.serviceId"
                :error-messages="errors"
                :hide-details="errors.length === 0"
                :items="services"
                label="Serviço"
                dense
                outlined
                item-text="name"
                item-value="id"
              />
            </validation-provider>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Comissão (%)"
              rules="required"
              vid="form.percentage"
            >
              <v-text-field
                v-model="form.percentage"
                :error-messages="errors"
                :hide-details="errors.length === 0"
                outlined
                dense
                suffix="%"
                label="Comissão (%)"
                type="number"
              />
            </validation-provider>
          </v-col>
        </v-row>
      </dialog-form>
    </validation-observer>

    <dialog-remove
      :status="removeDialogStatus"
      :text-to-confirm="form.name"
      @action="fetchActionRemove"
      @close="removeDialogStatus = !removeDialogStatus"
    >
    </dialog-remove>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mdiFileEdit, mdiMagnify, mdiTablePlus } from '@mdi/js'
import { ref } from '@vue/composition-api'

export default {
  setup() {
    const services = ref([])
    const filterServiceId = ref(null)
    const formDialogStatus = ref(false)
    const removeDialogStatus = ref(false)
    const formIsUpdate = ref(false)
    const form = ref({
      id: ref(null),
      serviceId: ref(null),
      percentage: ref(null),
    })

    return {
      filterServiceId,
      form,
      formDialogStatus,
      removeDialogStatus,
      formIsUpdate,
      icons: {
        mdiFileEdit,
        mdiMagnify,
        mdiTablePlus,
      },
      headers: [
        { text: 'Ações', value: 'action', align: 'center' },
        { text: 'ID', value: 'id' },
        { text: 'Serviço', value: 'serviceId' },
        { text: 'Comissão (%)', value: 'percentage', align: 'right' },
      ],
      services,
    }
  },

  computed: {
    ...mapState('affiliates', ['rows']),
  },

  async created() {
    this.services = await this.findServices()
    await this.fetchCommissions()
  },

  methods: {
    ...mapActions('services', { findServices: 'find' }),
    ...mapActions('affiliates', ['find', 'findById', 'create', 'update', 'remove']),

    resolveServiceName(serviceId) {
      const service = this.services.find(service => service.id === serviceId)

      if (service) {
        return service.name
      }

      return null
    },

    async openFormToCreate() {
      this.formDialogStatus = true
      this.formIsUpdate = false
      this.form = {
        id: null,
        serviceId: null,
        percentage: null,
      }
    },

    async openFormToEdit(rowId) {
      const affiliate = await this.findById(rowId)

      if (affiliate) {
        await this.$refs.formAffiliate.reset()
        this.formDialogStatus = true
        this.formIsUpdate = true
        this.form = {
          id: affiliate.id,
          serviceId: affiliate.serviceId,
          percentage: this.$money(affiliate.percentage, 0),
        }
      }
    },

    async fetchCommissions() {
      await this.find({
        serviceId: this.filterServiceId,
      })
    },

    async fetchAction(isUpdate) {
      const body = {
        serviceId: Number(this.form.serviceId),
        percentage: this.$strip(this.form.percentage),
      }

      if (await this.$refs.formAffiliate.validate()) {
        if (isUpdate) await this.update(body)
        else await this.create(body)

        this.fetchCommissions()
        this.formDialogStatus = false
      }
    },

    async fetchActionRemove() {
      try {
        if (this.form.id) {
          await this.remove(this.form.id)
        }
      } finally {
        this.fetchCommissions()
        this.formDialogStatus = false
        this.removeDialogStatus = false
      }
    },
  },
}
</script>
